import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  PhoneIcon,
  UserGroupIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { Accommodation } from '../../types';
import apiService from '../../services/api';
import AccommodationForm from './AccommodationForm';
import AccommodationDetails from './AccommodationDetails';

interface AccommodationsFilters {
  city: '' | 'مكة المكرمة' | 'المدينة المنورة';
  search: string;
  isActive?: boolean;
}

const Accommodations: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<AccommodationsFilters>({
    city: '',
    search: '',
    isActive: undefined
  });
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState<Accommodation | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const queryClient = useQueryClient();

  // Fetch accommodations
  const { data: accommodationsData, isLoading, error, refetch } = useQuery({
    queryKey: ['accommodations', currentPage, filters],
    queryFn: () => apiService.getAccommodations(currentPage, 20, filters),
    placeholderData: (previousData) => previousData
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteAccommodation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accommodations'] });
    },
    onError: (error: any) => {
      console.error('Delete accommodation error:', error);
    }
  });

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
  };

  const handleCityFilter = (city: AccommodationsFilters['city']) => {
    setFilters(prev => ({ ...prev, city }));
    setCurrentPage(1);
  };

  const handleStatusFilter = (isActive?: boolean) => {
    setFilters(prev => ({ ...prev, isActive }));
    setCurrentPage(1);
  };

  const handleEdit = (accommodation: Accommodation) => {
    setSelectedAccommodation(accommodation);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleView = (accommodation: Accommodation) => {
    setSelectedAccommodation(accommodation);
    setShowDetails(true);
  };

  const handleDelete = async (accommodation: Accommodation) => {
    if (window.confirm(`هل أنت متأكد من حذف ${accommodation.name}؟`)) {
      deleteMutation.mutate(accommodation._id);
    }
  };

  const handleAddNew = () => {
    setSelectedAccommodation(null);
    setIsEditing(false);
    setShowForm(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        نشط
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        غير نشط
      </span>
    );
  };

  const getCityBadge = (city: string) => {
    const colors = {
      'مكة المكرمة': 'bg-gold-100 text-gold-800',
      'المدينة المنورة': 'bg-green-100 text-green-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[city as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        <MapPinIcon className="w-3 h-3 ml-1" />
        {city}
      </span>
    );
  };

  const getRatingStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
        <span className="text-sm text-gray-600 mr-1">({rating})</span>
      </div>
    );
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">خطأ في تحميل البيانات</div>
        <button
          onClick={() => refetch()}
          className="btn-primary"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 rtl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة السكن</h1>
          <p className="text-gray-600">إدارة أماكن الإقامة والفنادق</p>
        </div>
        <button
          onClick={handleAddNew}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="w-5 h-5 ml-2" />
          إضافة سكن جديد
        </button>
      </div>

      {/* Filters — soft bar */}
      <div className="rounded-2xl border border-gray-100 bg-white/80 shadow-sm px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              بحث
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                placeholder="اسم السكن أو العنوان..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 pr-10 pl-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 transition"
              />
            </div>
          </div>

          <div className="w-full lg:w-44">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              المدينة
            </label>
            <select
              value={filters.city}
              onChange={(e) =>
                handleCityFilter(e.target.value as AccommodationsFilters['city'])
              }
              className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 px-3 text-sm text-gray-800 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 transition"
            >
              <option value="">كل المدن</option>
              <option value="مكة المكرمة">مكة المكرمة</option>
              <option value="المدينة المنورة">المدينة المنورة</option>
            </select>
          </div>

          <div className="w-full lg:w-auto">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              الحالة
            </label>
            <div className="flex flex-wrap gap-1.5 rounded-xl bg-gray-50/80 border border-gray-100 p-1">
              {(
                [
                  { value: undefined, label: 'الكل' },
                  { value: true, label: 'نشط' },
                  { value: false, label: 'غير نشط' },
                ] as const
              ).map((opt) => {
                const active = filters.isActive === opt.value;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => handleStatusFilter(opt.value)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm transition ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm font-medium'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">جاري التحميل...</p>
          </div>
        ) : accommodationsData?.data?.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">لا توجد أماكن سكن</p>
            <button
              onClick={handleAddNew}
              className="btn-primary mt-4"
            >
              إضافة أول مكان سكن
            </button>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المنشأة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الموقع
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      السعة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      التقييم
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      السعر
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الحالة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accommodationsData?.data?.map((accommodation) => (
                    <tr key={accommodation._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                              <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                            </div>
                          </div>
                          <div className="mr-4">
                            <div className="text-sm font-medium text-gray-900">
                              {accommodation.name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <PhoneIcon className="w-4 h-4 ml-1" />
                              {accommodation.contact.phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          {getCityBadge(accommodation.location)}
                          {accommodation.address?.district && (
                            <div className="text-sm text-gray-500 mt-1">
                              {accommodation.address.district}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <UserGroupIcon className="w-4 h-4 ml-1 text-gray-400" />
                          <span>{accommodation.totalCapacity || 0}</span>
                          <span className="text-gray-500 mr-1">شخص</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {accommodation.rooms?.length || 0} غرفة
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRatingStars(accommodation.rating?.overall || 3)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(accommodation.contract.pricePerNight)}
                        <div className="text-xs text-gray-500">لكل ليلة</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(accommodation.isActive)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <button
                            onClick={() => handleView(accommodation)}
                            className="text-blue-600 hover:text-blue-900"
                            title="عرض التفاصيل"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(accommodation)}
                            className="text-green-600 hover:text-green-900"
                            title="تعديل"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(accommodation)}
                            className="text-red-600 hover:text-red-900"
                            title="حذف"
                            disabled={deleteMutation.isPending}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {accommodationsData && accommodationsData.pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary"
                  >
                    السابق
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, accommodationsData.pagination.pages))}
                    disabled={currentPage === accommodationsData.pagination.pages}
                    className="btn-secondary"
                  >
                    التالي
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      عرض{' '}
                      <span className="font-medium">
                        {((currentPage - 1) * 20) + 1}
                      </span>{' '}
                      إلى{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * 20, accommodationsData.pagination.total)}
                      </span>{' '}
                      من{' '}
                      <span className="font-medium">{accommodationsData.pagination.total}</span>{' '}
                      نتيجة
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px space-x-reverse">
                      {Array.from({ length: accommodationsData.pagination.pages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <AccommodationForm
          accommodation={selectedAccommodation || undefined}
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedAccommodation(null);
            setIsEditing(false);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['accommodations'] });
          }}
        />
      )}

      {showDetails && selectedAccommodation && (
        <AccommodationDetails
          accommodation={selectedAccommodation}
          isOpen={showDetails}
          onClose={() => {
            setShowDetails(false);
            setSelectedAccommodation(null);
          }}
          onEdit={() => {
            setShowDetails(false);
            handleEdit(selectedAccommodation);
          }}
        />
      )}
    </div>
  );
};

export default Accommodations; 